import type { RestaurantEventTier } from '@shared/types/restaurants';

/**
 * Pure sold-out and capacity predicates shared by every screen that sells
 * event tickets, so the rules for what counts as sold out live in one place.
 */

export const isEventSoldOut = (
  tiers: Pick<RestaurantEventTier, 'ticketsSold'>[],
  eventCapacity: number,
): boolean => {
  const totalEventTicketsSold = tiers.reduce(
    (sum, tier) => sum + tier.ticketsSold,
    0,
  );
  return totalEventTicketsSold >= eventCapacity;
};

export const isTierSoldOut = (
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>,
): boolean => tier.ticketsSold >= tier.capacity;

export const getTierSpotsLeft = (
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>,
): number => tier.capacity - tier.ticketsSold;

/**
 * Tickets a tier can still sell: the fewer of what its capacity has left and
 * what its free tables can seat. `allowedQuantities` is what the server says
 * those tables can seat (already capped by the tier and the event), or `null`
 * when tables don't apply, so only the capacity counts.
 */
export const getTierSeatsLeft = (
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>,
  allowedQuantities?: number[] | null,
): number => {
  const spotsLeft = getTierSpotsLeft(tier);
  if (!allowedQuantities) {
    return spotsLeft;
  }
  return Math.min(spotsLeft, Math.max(0, ...allowedQuantities));
};

export const getEventSpotsLeft = (
  tiers: Pick<RestaurantEventTier, 'ticketsSold'>[],
  eventCapacity: number,
): number =>
  eventCapacity - tiers.reduce((sum, tier) => sum + tier.ticketsSold, 0);

/**
 * Most tickets one purchase of the tier can hold. Events are not bound by the
 * restaurant's party-size limit, only by what the tier and the event have left.
 */
export const getTierMaxTicketsPerPurchase = (
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>,
  tiers: Pick<RestaurantEventTier, 'ticketsSold'>[],
  eventCapacity: number,
): number =>
  Math.max(
    1,
    Math.min(getTierSpotsLeft(tier), getEventSpotsLeft(tiers, eventCapacity)),
  );

export const isTierUnavailable = (
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>,
  tiers: Pick<RestaurantEventTier, 'ticketsSold'>[],
  eventCapacity: number,
): boolean => isEventSoldOut(tiers, eventCapacity) || isTierSoldOut(tier);

export const isTierAtCapacityForGuestCount = (
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>,
  tiers: Pick<RestaurantEventTier, 'ticketsSold'>[],
  eventCapacity: number,
  guestCount: number,
): boolean => {
  if (isTierUnavailable(tier, tiers, eventCapacity)) {
    return false;
  }
  return guestCount > getTierSpotsLeft(tier);
};

export const areAllTiersUnavailable = (
  tiers: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>[],
  eventCapacity: number,
): boolean =>
  tiers.length > 0 &&
  (isEventSoldOut(tiers, eventCapacity) ||
    tiers.every((tier) => isTierSoldOut(tier)));

/**
 * The most tickets that can be held in one tier, given what is already held
 * in the others. The event's spots left cap the whole order, not each
 * tier. Events are not bound by the restaurant's party-size limit.
 */
export const getTierMaxQuantity = ({
  tier,
  tiers,
  eventCapacity,
  tierQuantity,
  totalGuestCount,
}: {
  tier: Pick<RestaurantEventTier, 'ticketsSold' | 'capacity'>;
  tiers: Pick<RestaurantEventTier, 'ticketsSold'>[];
  eventCapacity: number;
  tierQuantity: number;
  totalGuestCount: number;
}): number => {
  const orderSpotsLeft =
    getEventSpotsLeft(tiers, eventCapacity) - totalGuestCount;
  return Math.max(
    0,
    Math.min(getTierSpotsLeft(tier), tierQuantity + orderSpotsLeft),
  );
};
